import { lazy, ReactNode } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  redirect,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import ErrorPage from '@/components/error/ErrorPage';
import KycErrorPage from '@/components/error/KycErrorPage';
import {
  getLegacySupportingDocumentsRedirect,
  getOnboardingRouteHandle,
  getOnboardingStepDefinition,
  LEGACY_SUPPORTING_DOCUMENTS_ROUTE,
  OnboardingStep,
  OnboardingStepTitle,
} from '@/components/Onboarding/onboardingSteps';
import { basePath } from '@/Constants';
import { appRoutes as paths } from '@/constants/routes';

import PlainLayout from './PlainLayout';
import RestrictedRoute from './RestrictedRoute';

const AccountSettings = lazy(() => import('../pages/Settings/AccountSettings'));
const AllMarkets = lazy(() => import('../pages/Trade/Markets/AllMarkets'));
const BankAccounts = lazy(() => import('../pages/BankAccounts'));
const Campaign = lazy(() => import('../pages/Campaign'));
const ChatPage = lazy(() => import('../pages/Chat/index'));
const ClosedTrades = lazy(() => import('../pages/MyTrades/ClosedTrades'));
const CopiedKaptains = lazy(() => import('../pages/MyTrades/CopiedKaptains'));
const Deposit = lazy(() => import('../pages/Deposit'));
const DepositResponse = lazy(() => import('../pages/Deposit/DepositResponse'));
const InstrumentRedirect = lazy(() => import('../pages/Trade/Markets/InstrumentRedirect'));
const MostTraded = lazy(() => import('../pages/Trade/MostTraded'));
const MyProfile = lazy(() => import('../pages/MyProfile'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Notifications = lazy(() => import('../pages/Notifications'));
const OpenTrades = lazy(() => import('../pages/MyTrades/OpenTrades'));
const PendingTrades = lazy(() => import('../pages/MyTrades/PendingTrades'));
const Portfolio = lazy(() => import('../pages/MyTrades/Portfolio/Portfolio'));
const Profile = lazy(() => import('../pages/Profile'));
const RedirectToApp = lazy(() => import('../pages/RedirectToApp'));
const SearchResult = lazy(() => import('../pages/SearchResult'));
const StackedTrades = lazy(() => import('../pages/MyTrades/StackedTrades'));
const Support = lazy(() => import('../pages/Support/Support'));
const TopMovers = lazy(() => import('../pages/Trade/TopMovers'));
const TradeDetailOrder = lazy(() => import('../pages/MyTrades/TradeDetailOrder'));
const TradeDetailPosition = lazy(() => import('../pages/MyTrades/TradeDetailPosition'));
const TransactionHistory = lazy(() => import('../pages/TransactionHistory'));
const UserProfile = lazy(() => import('../pages/UserProfile'));
const Withdrawal = lazy(() => import('../pages/Withdrawal'));

const LegacyDepositRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/deposit/, paths.transactions.deposit)}${search}${hash}`} replace />;
};

const LegacyTransactionHistoryRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`${paths.transactions.history}${search}${hash}`} replace />;
};

export type AuthType = 'AuthenticatedOnly' | 'UnauthenticatedOnly' | 'Public';

/**
 * @note Any public route addition or modification please also update the sitemapbuilder
 * @deprecated for any new route please add to createBrowserRouter
 * @todo migrate all routes to createBrowserRouter
 */
const appRoutes: Array<{ paths: Array<string>; element?: ReactNode; strict?: boolean; auth: AuthType }> = [
  {
    paths: [paths.bankAccounts],
    element: <BankAccounts />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/campaigns/*'],
    element: <Campaign />,
    auth: 'Public',
  },
  {
    paths: ['/chats/:cid?'],
    element: <ChatPage />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: [
      paths.transactions.deposit,
      `${paths.transactions.deposit}/:pageType`,
      `${paths.transactions.deposit}/:pageType/:depositCurrency`,
      `${paths.transactions.deposit}/:pageType/:depositCurrency/:step_`,
      `${paths.transactions.deposit}/:pageType/:depositCurrency/:step_/:paymentCode`,
    ],
    element: <Deposit />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: [
      '/deposit',
      '/deposit/:pageType',
      '/deposit/:pageType/:depositCurrency',
      '/deposit/:pageType/:depositCurrency/:step_',
      '/deposit/:pageType/:depositCurrency/:step_/:paymentCode',
    ],
    element: <LegacyDepositRedirect />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/deposit-response'],
    strict: true,
    element: <DepositResponse />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/my-trades/closed-trades'],
    element: <ClosedTrades />,
    strict: true,
    auth: 'Public',
  },
  {
    paths: ['/my-trades/closed-trades/:transactionId', '/my-trades/closed-trades/kaptain/:kaptainCognitoId'],
    element: <ClosedTrades />,
    strict: true,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/my-trades/copied-kaptains', '/my-trades/copied-kaptains/:cognitoId'],
    element: <CopiedKaptains />,
    strict: true,
    auth: 'Public',
  },
  {
    paths: ['/my-trades/open-trades', '/my-trades/open-trades/kaptain/:kaptainCognitoId'],
    element: <OpenTrades />,
    strict: true,
    auth: 'Public',
  },
  {
    paths: ['/my-trades/open-trades/stacked-trades/:instrumentKey'],
    element: <StackedTrades />,
    strict: true,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/my-trades/open-trades/:positionId', '/my-trades/open-trades/stacked-trades/:instrumentKey/:positionId'],
    element: <TradeDetailPosition />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/my-trades/pending-trades'],
    element: <PendingTrades />,
    strict: true,
    auth: 'Public',
  },
  {
    paths: ['/my-trades/pending-trades/:orderId'],
    element: <TradeDetailOrder />,
    auth: 'Public',
  },
  {
    paths: ['/my-trades/portfolio'],
    element: <Portfolio />,
    auth: 'Public',
  },
  {
    paths: ['/my-profile/*'],
    element: <MyProfile />,
    auth: 'Public',
  },
  {
    paths: ['/not-found'],
    element: <NotFound />,
    auth: 'Public',
  },
  {
    paths: ['/notifications'],
    element: <Notifications />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/profile/:cognitoId/*'],
    element: <Profile />,
    auth: 'Public',
  },
  {
    paths: ['/search/:searchInput'],
    element: <SearchResult />,
    auth: 'Public',
  },
  {
    paths: ['/settings/*'],
    element: <AccountSettings />,
    auth: 'Public',
  },
  {
    paths: ['/support/*'],
    element: <Support />,
    auth: 'Public',
  },
  {
    paths: ['/trade/markets/:instrumentKey/*'],
    element: <InstrumentRedirect />,
    auth: 'Public',
  },
  {
    paths: ['/trade/markets/'],
    element: <AllMarkets />,
    auth: 'Public',
  },
  {
    paths: ['/trade/most-traded'],
    element: <MostTraded />,
    auth: 'Public',
  },
  {
    paths: ['/trade/top-movers'],
    element: <TopMovers />,
    auth: 'Public',
  },
  {
    paths: [paths.transactions.root],
    element: <Navigate to={paths.transactions.deposit} replace />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: [paths.transactions.history],
    element: <TransactionHistory />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: [paths.legacyTransactionHistory],
    element: <LegacyTransactionHistoryRedirect />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/user/:friendlyName/*'],
    element: <UserProfile />,
    auth: 'Public',
  },
  {
    paths: [paths.transactions.withdrawal],
    element: <Withdrawal />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/withdrawal'],
    element: <Navigate to={paths.transactions.withdrawal} replace />,
    auth: 'AuthenticatedOnly',
  },
  {
    paths: ['/app-redirect'],
    element: <RedirectToApp />,
    auth: 'Public',
  },
];

/**
 * @deprecated will be replaced by createBrowserRouter, do not add new routes here
 */
const Root = () => {
  return (
    <Routes>
      {appRoutes.map(({ auth, paths, element, ...rest }) =>
        paths.map(path => (
          <Route
            key={path}
            path={path}
            element={auth === 'Public' ? element : <RestrictedRoute auth={auth}>{element}</RestrictedRoute>}
            {...rest}
          />
        ))
      )}
      <Route path='*' element={<Navigate to='/discover/dashboard' replace />} />
    </Routes>
  );
};

const migrateRoutes = (
  <Route
    path='onboard/upgrade'
    errorElement={<ErrorPage />}
    lazy={async () => {
      const { UpgradeLayout } = await import('./upgrade');
      return {
        element: <UpgradeLayout />,
      };
    }}
  >
    <Route
      path=':fromAccountType/:toAccountType'
      lazy={async () => {
        const MigrateLiveAccount = (await import('@/pages/Migrate/MigrateLiveAccount')).default;
        const { upgradeLoader } = await import('./upgrade');
        return {
          loader: upgradeLoader,
          element: <MigrateLiveAccount />,
        };
      }}
    />
    <Route
      path='result'
      lazy={async () => {
        const MigrationResult = (await import('@/pages/Migrate/MigrationResult')).default;
        return {
          element: <MigrationResult />,
        };
      }}
    />
  </Route>
);

const kycRoutes = (
  <Route
    id='kyc'
    path='onboard'
    errorElement={<KycErrorPage />}
    shouldRevalidate={({ currentUrl }) => currentUrl.pathname === '/onboard/result'}
    lazy={async () => {
      const { kycLoader, KycLayout } = await import('./kyc');
      return {
        loader: kycLoader,
        element: <KycLayout />,
      };
    }}
  >
    {/* New-design steps share one persistent split-screen shell so the brand panel
        does not remount/flicker when navigating between steps. */}
    <Route
      lazy={async () => {
        const OnboardingShell = (await import('@/components/Onboarding/OnboardingShell')).default;
        return {
          element: <OnboardingShell />,
        };
      }}
    >
      <Route
        index
        lazy={async () => {
          const IndexPage = (await import('@/components/KYC/OnBoard/SignUpLiveAccount')).default;
          return {
            element: <IndexPage />,
          };
        }}
        handle={{
          title: OnboardingStepTitle.SignUp,
          currentStep: 0,
          nextRoute: getOnboardingStepDefinition(OnboardingStep.AccountName).route,
        }}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.AccountName).path}
        lazy={async () => {
          const AccountName = (await import('@/pages/KYC/account/AccountName')).default;
          return {
            element: <AccountName />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.AccountName)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.AccountResidence).path}
        lazy={async () => {
          const AccountResidence = (await import('@/pages/KYC/account/AccountResidence')).default;
          return {
            element: <AccountResidence />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.AccountResidence)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.AccountBirth).path}
        lazy={async () => {
          const AccountBirth = (await import('@/pages/KYC/account/AccountBirth')).default;
          return {
            element: <AccountBirth />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.AccountBirth)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.AccountTax).path}
        lazy={async () => {
          const AccountTax = (await import('@/pages/KYC/account/AccountTax')).default;
          return {
            element: <AccountTax />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.AccountTax)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.EconomicWealth).path}
        lazy={async () => {
          const EconomicWealth = (await import('@/pages/KYC/profile/EconomicWealth')).default;
          return {
            element: <EconomicWealth />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.EconomicWealth)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.EconomicFunding).path}
        lazy={async () => {
          const EconomicFunding = (await import('@/pages/KYC/profile/EconomicFunding')).default;
          return {
            element: <EconomicFunding />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.EconomicFunding)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.EconomicExperience).path}
        lazy={async () => {
          const EconomicExperience = (await import('@/pages/KYC/profile/EconomicExperience')).default;
          return {
            element: <EconomicExperience />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.EconomicExperience)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.EconomicKnowledge).path}
        lazy={async () => {
          const EconomicKnowledge = (await import('@/pages/KYC/profile/EconomicKnowledge')).default;
          return {
            element: <EconomicKnowledge />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.EconomicKnowledge)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.ProfilePep).path}
        lazy={async () => {
          const ProfilePep = (await import('@/pages/KYC/profile/ProfilePep')).default;
          return {
            element: <ProfilePep />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.ProfilePep)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.VerifyAddress).path}
        lazy={async () => {
          const VerifyAddress = (await import('@/pages/KYC/verify/VerifyAddress')).default;
          return {
            element: <VerifyAddress />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.VerifyAddress)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.VerifyIdentity).path}
        lazy={async () => {
          const VerifyIdentity = (await import('@/pages/KYC/verify/VerifyIdentity')).default;
          return {
            element: <VerifyIdentity />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.VerifyIdentity)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.VerifyTerms).path}
        lazy={async () => {
          const VerifyTerms = (await import('@/pages/KYC/verify/VerifyTerms')).default;
          return {
            element: <VerifyTerms />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.VerifyTerms)}
      />
      <Route
        path={getOnboardingStepDefinition(OnboardingStep.VerifyDeclarations).path}
        lazy={async () => {
          const VerifyDeclarations = (await import('@/pages/KYC/verify/VerifyDeclarations')).default;
          return {
            element: <VerifyDeclarations />,
          };
        }}
        handle={getOnboardingRouteHandle(OnboardingStep.VerifyDeclarations)}
      />
    </Route>
    <Route
      path='result'
      lazy={async () => {
        const Result = (await import('@/pages/KYC/Result')).default;
        return {
          element: <Result />,
        };
      }}
      handle={{ title: OnboardingStepTitle.Done }}
    />
  </Route>
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      {migrateRoutes}
      <Route
        path={LEGACY_SUPPORTING_DOCUMENTS_ROUTE}
        loader={({ request }) => redirect(getLegacySupportingDocumentsRedirect(new URL(request.url)))}
      />
      {kycRoutes}
      <Route
        lazy={async () => {
          const AllTheProviders = (await import('@/contexts/AllTheProviders')).default;
          return {
            element: (
              <AllTheProviders>
                <Outlet />
              </AllTheProviders>
            ),
          };
        }}
        errorElement={<ErrorPage />}
      >
        <Route element={<PlainLayout />} errorElement={<ErrorPage />}>
          {/* plain layout routes put here */}
          <Route
            path='login'
            lazy={async () => {
              const Login = (await import('../pages/Login/Login')).default;
              return {
                element: (
                  <RestrictedRoute auth='UnauthenticatedOnly'>
                    <Login />
                  </RestrictedRoute>
                ),
              };
            }}
          />
          <Route
            path='signup'
            lazy={async () => {
              const SignUp = (await import('../pages/SignUp/SignUp')).default;
              return {
                element: (
                  <RestrictedRoute auth='UnauthenticatedOnly'>
                    <SignUp />
                  </RestrictedRoute>
                ),
              };
            }}
          />
          <Route path='signup/*' element={<Navigate to='/signup' replace />} />
          <Route
            path='forgot-password'
            lazy={async () => {
              const ForgotPassword = (await import('../pages/ForgotPassword/ForgotPassword')).default;
              return {
                element: (
                  <RestrictedRoute auth='UnauthenticatedOnly'>
                    <ForgotPassword />
                  </RestrictedRoute>
                ),
              };
            }}
          />
          <Route
            path='support/faq-plain'
            lazy={async () => {
              const FAQ = (await import('../pages/Support/FAQ')).default;
              return {
                element: (
                  <div style={{ backgroundColor: 'white', height: '100vh' }}>
                    <FAQ />
                  </div>
                ),
              };
            }}
          />
          <Route
            path='reset-password'
            lazy={async () => {
              const ResetPassword = (await import('../pages/Verify/ResetPassword')).default;
              return {
                element: (
                  <RestrictedRoute auth='UnauthenticatedOnly'>
                    <ResetPassword />
                  </RestrictedRoute>
                ),
              };
            }}
          />
          <Route
            path='reset-password-success'
            lazy={async () => {
              const ResetPasswordSuccess = (await import('../pages/Verify/ResetPasswordSuccess')).default;
              return {
                element: (
                  <RestrictedRoute auth='UnauthenticatedOnly'>
                    <ResetPasswordSuccess />
                  </RestrictedRoute>
                ),
              };
            }}
          />
        </Route>
        <Route
          lazy={async () => {
            const RootLayout = (await import('./RootLayout')).default;
            return {
              element: (
                <RestrictedRoute auth='AuthenticatedOnly'>
                  <RootLayout />
                </RestrictedRoute>
              ),
            };
          }}
        >
          <Route path='discover'>
            <Route
              path='blog/:slug'
              lazy={async () => {
                const BlogDetail = (await import('../pages/Discover/BlogDetail')).default;
                return {
                  element: <BlogDetail />,
                };
              }}
            />
            <Route
              path='calendar/:eventId?'
              lazy={async () => {
                const Calendar = (await import('../pages/Calendar')).default;
                return {
                  element: <Calendar />,
                };
              }}
            />
            <Route
              path='dashboard'
              lazy={async () => {
                const Dashboard = (await import('../pages/Discover/Dashboard/Dashboard')).default;
                return {
                  element: <Dashboard />,
                };
              }}
            />
            <Route path='feeds'>
              <Route
                path='hashtag/:tagId'
                lazy={async () => {
                  const HashTag = (await import('../pages/HashTag')).default;
                  return {
                    element: <HashTag />,
                  };
                }}
              />
              <Route
                path=':feedGroup/posts/:activityId'
                lazy={async () => {
                  const CustomSinglePost = (await import('../components/CustomSinglePost')).default;
                  return {
                    element: <CustomSinglePost />,
                  };
                }}
              />
              <Route
                path=':feedGroup?'
                lazy={async () => {
                  const SocialFeed = (await import('../pages/Discover/SocialFeed')).default;
                  return {
                    element: <SocialFeed />,
                  };
                }}
              />
            </Route>
            <Route
              path='leaderboard'
              lazy={async () => {
                const Leaderboard = (await import('../pages/Discover/Leaderboard/Leaderboard')).default;
                return {
                  element: <Leaderboard />,
                };
              }}
            />
            <Route
              path='news'
              lazy={async () => {
                const News = (await import('../pages/Discover/News')).default;
                return {
                  element: <News />,
                };
              }}
            />
          </Route>
          <Route path='*' element={<Root />} errorElement={<ErrorPage />} />
        </Route>
      </Route>
    </Route>
  ),
  {
    basename: basePath,
  }
);
